/**
 * Purpose: Dice rolling logic and video asset mapping.
 * Owns: Random number generation (1-6), dice video file paths, fallback handling.
 * Notes: Six pre-rendered dice videos will be placed in /src/assets/videos/.
 *        Until those videos exist, the UI should show a CSS placeholder animation.
 *        The dice value also determines the point value of a correct answer.
 */

/** Valid dice outcomes. */
export type DiceValue = 1 | 2 | 3 | 4 | 5 | 6;

/** Minimum dice value (inclusive). */
const DICE_MIN = 1;
/** Maximum dice value (inclusive). */
const DICE_MAX = 6;

// _______________________________________________
//              RANDOM ROLL
// _______________________________________________

/** Generate a random dice roll between 1 and 6. */
export function rollDice(): DiceValue {
  return (Math.floor(Math.random() * DICE_MAX) + DICE_MIN) as DiceValue;
}

// _______________________________________________
//              VIDEO ASSET MAPPING
// _______________________________________________

/** Path pattern for dice roll videos. Videos named dice-1.mp4 through dice-6.mp4. */
const DICE_VIDEO_PATH_PREFIX = '/src/assets/videos/dice-';
const DICE_VIDEO_EXTENSION = '.mp4';

/** Get the video file path for a given dice value. */
export function getDiceVideoPath(value: DiceValue): string {
  return `${DICE_VIDEO_PATH_PREFIX}${value}${DICE_VIDEO_EXTENSION}`;
}

/**
 * Check whether the dice video files are available.
 * Returns false if the video fails to load — the UI should fall back to
 * a CSS placeholder animation in that case.
 */
export async function isDiceVideoAvailable(value: DiceValue): Promise<boolean> {
  try {
    const response = await fetch(getDiceVideoPath(value), { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}
